class CartRemoveButton extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('click', (event) => {
      event.preventDefault();
      const cartItems = this.closest('cart-items') || this.closest('cart-drawer-items');
      if (!cartItems || typeof cartItems.updateQuantity !== 'function') return;
      // Remove item by setting quantity to 0
      cartItems.updateQuantity(this.dataset.index, 0);
    });
  }
}

customElements.define('cart-remove-button', CartRemoveButton);

class CartItems extends HTMLElement {
  constructor() {
    super();
    this.lineItemStatusElement =
      document.getElementById('shopping-cart-line-item-status') ||
      document.getElementById('CartDrawer-LineItemStatus');

    // Assuming debounce and ON_CHANGE_DEBOUNCE_TIMER are defined globally
    const debouncedOnChange = debounce((event) => {
      this.onChange(event);
    }, ON_CHANGE_DEBOUNCE_TIMER);

    this.addEventListener('change', debouncedOnChange.bind(this));
  }

  cartUpdateUnsubscriber = undefined;

  connectedCallback() {
    // Subscribe to cart updates globally to handle external changes (like add-to-cart form)
    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (event.source === 'cart-items') {
        return; // Don't re-render if this component initiated the change
      }
      this.onCartUpdate();
    });
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
  }

  onChange(event) {
    this.updateQuantity(
      event.target.dataset.index,
      event.target.value,
      document.activeElement.getAttribute('name')
    );
  }

  onCartUpdate() {
    // Fetch fresh cart HTML content to update the display
    fetch(`${routes.cart_url}?section_id=main-cart-items`)
      .then((response) => response.text())
      .then((responseText) => {
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        const sourceQty = html.querySelector('cart-items');
        if (sourceQty) {
          this.innerHTML = sourceQty.innerHTML;
        }
        // Recalc milestones after full cart refresh
        if (window.initCartMilestones) {
          window.initCartMilestones(document);
        }
      })
      .catch((e) => {
        console.error('Error fetching cart content on update:', e);
      });
  }

  getSectionsToRender() {
    return [
      {
        id: 'main-cart-items',
        section: document.getElementById('main-cart-items').dataset.id,
        selector: '.js-contents',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
      {
        id: 'cart-live-region-text',
        section: 'cart-live-region-text',
        selector: '.shopify-section',
      },
      {
        id: 'main-cart-footer',
        section: document.getElementById('main-cart-footer').dataset.id,
        selector: '.js-contents',
      },
    ];
  }

  updateQuantity(line, quantity, name) {
    this.enableLoading(line);

    const body = JSON.stringify({
      line,
      quantity,
      sections: this.getSectionsToRender().map((section) => section.section),
      sections_url: window.location.pathname,
    });

    fetch(`${routes.cart_change_url}`, { ...fetchConfig(), ...{ body } })
      .then((response) => {
        return response.text();
      })
      .then((state) => {
        const parsedState = JSON.parse(state);
        const quantityElement =
          document.getElementById(`Quantity-${line}`) ||
          document.getElementById(`Drawer-quantity-${line}`);
        const items = document.querySelectorAll('.cart-item');

        if (parsedState.errors) {
          if (quantityElement) {
            quantityElement.value = quantityElement.getAttribute('value');
          }
          this.updateLiveRegions(line, parsedState.errors);
          return;
        }

        this.classList.toggle('is-empty', parsedState.item_count === 0);
        const cartDrawerWrapper = document.querySelector('cart-drawer');
        const cartFooter = document.getElementById('main-cart-footer');

        if (cartFooter) cartFooter.classList.toggle('is-empty', parsedState.item_count === 0);
        if (cartDrawerWrapper)
          cartDrawerWrapper.classList.toggle('is-empty', parsedState.item_count === 0);

        this.getSectionsToRender().forEach((section) => {
          const elementToReplace =
            document.getElementById(section.id).querySelector(section.selector) ||
            document.getElementById(section.id);

          if (!elementToReplace) return;

          elementToReplace.innerHTML = this.getSectionInnerHTML(
            parsedState.sections[section.section],
            section.selector
          );
        });

        const updatedValue = parsedState.items[line - 1]
          ? parsedState.items[line - 1].quantity
          : undefined;
        let message = '';
        if (items.length === parsedState.items.length && quantityElement) {
          if (updatedValue !== parseInt(quantityElement.value, 10)) {
            if (typeof updatedValue === 'undefined') {
              message = window.cartStrings.error;
            } else {
              message = window.cartStrings.quantityError.replace('[quantity]', updatedValue);
            }
          }
        }
        this.updateLiveRegions(line, message);

        const lineItem =
          document.getElementById(`CartItem-${line}`) ||
          document.getElementById(`CartDrawer-Item-${line}`);
        const cartDrawerWrapper2 = document.querySelector('cart-drawer');

        if (lineItem && lineItem.querySelector(`[name="${name}"]`)) {
          cartDrawerWrapper2
            ? trapFocus(cartDrawerWrapper2, lineItem.querySelector(`[name="${name}"]`))
            : lineItem.querySelector(`[name="${name}"]`).focus();
        } else if (parsedState.item_count === 0 && cartDrawerWrapper2) {
          trapFocus(
            cartDrawerWrapper2.querySelector('.drawer__inner-empty'),
            cartDrawerWrapper2.querySelector('a')
          );
        } else if (document.querySelector('.cart-item') && cartDrawerWrapper2) {
          trapFocus(cartDrawerWrapper2, document.querySelector('.cart-item__name'));
        }

        // 1. Tell the rest of the theme the cart changed
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items' });

        // 2. Force the Milestones Section to re-render and recalculate
        if (window.renderDrawerMilestones) {
          window.renderDrawerMilestones();
        }
      })
      .catch(() => {
        this.querySelectorAll('.loading-overlay').forEach((overlay) =>
          overlay.classList.add('hidden')
        );
        const errors =
          document.getElementById('cart-errors') ||
          document.getElementById('CartDrawer-CartErrors');
        if (errors) errors.textContent = window.cartStrings.error;
      })
      .finally(() => {
        this.disableLoading(line);
      });
  }

  updateLiveRegions(line, message) {
    const lineItemError =
      document.getElementById(`Line-item-error-${line}`) ||
      document.getElementById(`CartDrawer-LineItemError-${line}`);
    if (lineItemError) {
      const textEl = lineItemError.querySelector('.cart-item__error-text');
      if (textEl) textEl.innerHTML = message;
    }

    if (!this.lineItemStatusElement) return;

    this.lineItemStatusElement.setAttribute('aria-hidden', true);

    const cartStatus =
      document.getElementById('cart-live-region-text') ||
      document.getElementById('CartDrawer-LiveRegionText');
    if (!cartStatus) return;

    cartStatus.setAttribute('aria-hidden', false);

    setTimeout(() => {
      cartStatus.setAttribute('aria-hidden', true);
    }, 1000);
  }

  getSectionInnerHTML(html, selector) {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const el = doc.querySelector(selector);
    return el ? el.innerHTML : '';
  }

  enableLoading(line) {
    const mainCartItems =
      document.getElementById('main-cart-items') ||
      document.getElementById('CartDrawer-CartItems');
    if (mainCartItems) {
      mainCartItems.classList.add('cart__items--disabled');
    }

    const cartItemElements = this.querySelectorAll(`#CartItem-${line} .loading-overlay`);
    const cartDrawerItemElements = this.querySelectorAll(
      `#CartDrawer-Item-${line} .loading-overlay`
    );

    [...cartItemElements, ...cartDrawerItemElements].forEach((overlay) =>
      overlay.classList.remove('hidden')
    );

    if (document.activeElement) document.activeElement.blur();
    if (this.lineItemStatusElement) {
      this.lineItemStatusElement.setAttribute('aria-hidden', false);
    }
  }

  disableLoading(line) {
    const mainCartItems =
      document.getElementById('main-cart-items') ||
      document.getElementById('CartDrawer-CartItems');
    if (mainCartItems) {
      mainCartItems.classList.remove('cart__items--disabled');
    }

    const cartItemElements = this.querySelectorAll(`#CartItem-${line} .loading-overlay`);
    const cartDrawerItemElements = this.querySelectorAll(
      `#CartDrawer-Item-${line} .loading-overlay`
    );

    cartItemElements.forEach((overlay) => overlay.classList.add('hidden'));
    cartDrawerItemElements.forEach((overlay) => overlay.classList.add('hidden'));
  }
}

customElements.define('cart-items', CartItems);

if (!customElements.get('cart-note')) {
  customElements.define(
    'cart-note',
    class CartNote extends HTMLElement {
      constructor() {
        super();

        this.addEventListener(
          'change',
          debounce((event) => {
            const body = JSON.stringify({ note: event.target.value });
            fetch(`${routes.cart_update_url}`, { ...fetchConfig(), ...{ body } });
          }, ON_CHANGE_DEBOUNCE_TIMER)
        );
      }
    }
  );
}

/* ============================================================
   Cart Milestones: bar + circles + modal
   ============================================================ */
(function () {
  // --- Simple /cart.js fetch helper ---
  function fetchCart(callback) {
    fetch('/cart.js', { credentials: 'same-origin' })
      .then(function (res) {
        return res.json();
      })
      .then(function (cart) {
        callback(cart);
      })
      .catch(function (err) {
        console.warn('Milestones cart fetch failed', err);
      });
  }

  // Modal opener (with optional reward selector + image preview)
  function openMilestoneModal(root, milestone) {
  var modal = root.querySelector('[data-role="modal"]');
  if (!modal) return;

  var titleEl = modal.querySelector('[data-role="modal-title"]');
  var subtitleEl = modal.querySelector('[data-role="modal-subtitle"]');
  var bodyEl = modal.querySelector('[data-role="modal-body"]');
  var imgEl = modal.querySelector('[data-role="modal-image"]');

  if (titleEl) {
    titleEl.textContent = milestone.title || '';
  }

  if (subtitleEl) {
    if (milestone.unlocked) {
      subtitleEl.textContent = 'Reward unlocked!';
    } else if (milestone.money) {
      subtitleEl.textContent = 'Spend ' + milestone.money + ' to unlock.';
    } else {
      subtitleEl.textContent = '';
    }
  }

  if (bodyEl) {
    bodyEl.textContent = milestone.description || '';
  }

  // Remove any previous reward UI
  var oldReward = modal.querySelector('[data-role="reward-root"]');
  if (oldReward && oldReward.parentNode) {
    oldReward.parentNode.removeChild(oldReward);
  }

  // If unlocked and we have a reward template, inject it
  if (milestone.unlocked && milestone.templateId) {
    var tpl = root.querySelector('#' + milestone.templateId);
    if (tpl && 'content' in tpl) {
      var rewardRoot = document.createElement('div');
      rewardRoot.setAttribute('data-role', 'reward-root');
      rewardRoot.appendChild(tpl.content.cloneNode(true));

      if (bodyEl && bodyEl.parentNode) {
        bodyEl.parentNode.insertBefore(rewardRoot, bodyEl.nextSibling);
      } else {
        modal.appendChild(rewardRoot);
      }

      var selectEl   = rewardRoot.querySelector('[data-role="reward-select"]');
      var addBtn     = rewardRoot.querySelector('[data-role="reward-add"]');

      // Be tolerant: find the thumb image even if data-role is missing
      var previewImg = rewardRoot.querySelector('[data-role="reward-preview-image"], .cm-reward-thumb img');
      var previewBox = previewImg ? previewImg.closest('.cm-reward-thumb') : null;

      function updatePreview() {
        if (!previewImg || !selectEl) return;
        var opt = selectEl.options[selectEl.selectedIndex];
        if (!opt) return;

        var src   = opt.getAttribute('data-image');
        var label = (opt.textContent || '').trim();

        if (src) {
          previewImg.src = src;
          previewImg.alt = label;
          if (previewBox) previewBox.style.display = 'block'; // force visible
        } else {
          previewImg.removeAttribute('src');
          previewImg.alt = '';
          if (previewBox) previewBox.style.display = 'none';
        }
      }


      function updatePreview() {
        if (!previewImg || !selectEl) return;
        var opt = selectEl.options[selectEl.selectedIndex];
        if (!opt) return;

        var src   = opt.getAttribute('data-image');
        var label = (opt.textContent || '').trim();

        if (src) {
          previewImg.src = src;
          previewImg.alt = label;
          if (previewBox) previewBox.style.display = 'block';   // 👈 force visible
        } else {
          previewImg.removeAttribute('src');
          previewImg.alt = '';
          if (previewBox) previewBox.style.display = 'none';
        }
      }

      if (selectEl) {
        selectEl.addEventListener('change', updatePreview);
        updatePreview(); // initial preview
      }

      if (selectEl && addBtn) {
        addBtn.addEventListener('click', function () {
          var variantId = selectEl.value;
          if (!variantId) return;
          if (window.cmAddRewardToCart) {
            window.cmAddRewardToCart(variantId, milestone.title);
          }
        });
      }
    }
  }

  // Image logic for main modal image
  if (imgEl) {
    if (milestone.image) {
      imgEl.src = milestone.image;
    } else if (milestone.circle_icon) {
      imgEl.src = milestone.circle_icon;
    } else {
      imgEl.removeAttribute('src');
    }
  }

  modal.classList.add('is-open');
}


  // Super-simple helper: add reward, then reload the page so cart + drawer are fresh
  window.cmAddRewardToCart = function (variantId, label) {
    if (!variantId) return;

    var payload = {
      items: [
        {
          id: Number(variantId),
          quantity: 1,
          properties: {
            _free_gift: label || 'Milestone reward',
          },
        },
      ],
    };

    fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      credentials: 'same-origin',
      body: JSON.stringify(payload),
    })
      .then(function (res) {
        if (!res.ok) throw new Error('Add to cart failed');
        return res.json();
      })
      .then(function () {
        // Hard refresh so cart drawer and milestones are in sync
        window.location.reload();
      })
      .catch(function (err) {
        console.warn('Failed to add reward item', err);
        // Even on error, reload to avoid stale UI
        window.location.reload();
      });
  };

  function attachModalClose(root) {
    if (root.dataset.cmModalBound === '1') return;
    root.dataset.cmModalBound = '1';

    var modal = root.querySelector('[data-role="modal"]');
    if (!modal) return;

    modal.querySelectorAll('[data-role="modal-close"]').forEach(function (el) {
      el.addEventListener('click', function () {
        modal.classList.remove('is-open');
      });
    });

    document.addEventListener('keydown', function (evt) {
      if (evt.key === 'Escape') modal.classList.remove('is-open');
    });
  }

  function recalcRoot(root, cart) {
    if (!root) return;

    var headlineEl = root.querySelector('[data-role="headline"]');
    var fillEl = root.querySelector('[data-role="fill"]');
    var markersEl = root.querySelector('[data-role="markers"]');
    var dataEls = Array.prototype.slice.call(
      root.querySelectorAll('.cm-milestone-data')
    );

    if (!dataEls.length || !fillEl || !markersEl) return;

    var subtotalCents = cart ? cart.items_subtotal_price || cart.total_price || 0 : 0;
    var subtotalMajor = subtotalCents / 100;

    var milestones = dataEls
      .map(function (el) {
        var spend = parseFloat(el.getAttribute('data-m-spend')) || 0;
        return {
          spend: spend,
          title: el.getAttribute('data-m-title') || '',
          description: el.getAttribute('data-m-description') || '',
          money: el.getAttribute('data-m-money') || '',
          circle_icon: el.getAttribute('data-m-circle') || '',
          image: el.getAttribute('data-m-image') || '',
          templateId: el.getAttribute('data-m-template-id') || '',
          unlocked: subtotalMajor >= spend,
        };
      })
      .sort(function (a, b) {
        return a.spend - b.spend;
      });

    var largest = 0;
    var hasNext = false;
    var nextTitle = '';
    var nextTarget = 0;

    milestones.forEach(function (m) {
      if (m.spend > largest) largest = m.spend;
      if (!hasNext && m.spend > subtotalMajor) {
        hasNext = true;
        nextTitle = m.title || 'your next reward';
        nextTarget = m.spend;
      }
    });

    if (!hasNext && largest > 0) {
      nextTarget = largest;
    }

    // ----- dots -----
    markersEl.innerHTML = '';
    if (largest > 0) {
      milestones.forEach(function (m) {
        var marker = document.createElement('button');
        marker.type = 'button';
        marker.className = 'cm-progress-marker';

        var pos = (m.spend / largest) * 100;
        marker.style.left = pos + '%';

        if (m.spend <= subtotalMajor) {
          marker.classList.add('is-complete');
        }
        if (hasNext && m.spend === nextTarget && subtotalMajor < largest) {
          marker.classList.add('is-next-marker');
        }

        if (m.circle_icon) {
          marker.style.backgroundImage = 'url(' + m.circle_icon + ')';
          marker.style.setProperty('--cm-marker-image', 'url(' + m.circle_icon + ')');
        }

        marker.addEventListener('click', function () {
          openMilestoneModal(root, m);
        });

        markersEl.appendChild(marker);
      });
    }

    // ----- bar fill (overall progress) -----
    var pct = 0;
    if (largest > 0) pct = (subtotalMajor / largest) * 100;
    pct = Math.max(0, Math.min(100, Math.floor(pct)));
    var visiblePct = pct > 0 && pct < 2 ? 2 : pct; // tiny bar still visible
    fillEl.style.width = visiblePct + '%';

    // ----- headline text (to next milestone) -----
    var pctToNext = 100;
    if (hasNext && nextTarget > 0) {
      pctToNext = (subtotalMajor / nextTarget) * 100;
    }
    pctToNext = Math.max(0, Math.min(100, Math.floor(pctToNext)));

    if (headlineEl) {
      if (hasNext && subtotalMajor < largest) {
        headlineEl.textContent =
          "Keep spending! You're " + pctToNext + '% of the way to ' + nextTitle + '!';
      } else {
        headlineEl.textContent = '🎉 All milestones unlocked!';
      }
    }

    attachModalClose(root);
  }

  // Fetch cart once and update all milestones in the given scope
  function refreshCartMilestones(scope) {
    var context = scope || document;
    var roots = Array.prototype.slice.call(
      context.querySelectorAll('[data-cart-milestones]')
    );

    // If roots are missing, try initializing the section first (drawer injection)
    if (!roots.length && window.renderDrawerMilestones) {
      window.renderDrawerMilestones();
      return;
    }
    if (!roots.length) return;

    fetchCart(function (cart) {
      roots.forEach(function (root) {
        recalcRoot(root, cart);
      });
    });
  }

  // Expose globally so cart-drawer can call it when it injects the section
  window.initCartMilestones = refreshCartMilestones;

  // Initial run on page load (for cart / offers page etc.)
  document.addEventListener('DOMContentLoaded', function () {
    refreshCartMilestones(document);
  });

  // Hook into Dawn's cart pub/sub so it updates AFTER any cart change
  if (typeof subscribe === 'function' && window.PUB_SUB_EVENTS && PUB_SUB_EVENTS.cartUpdate) {
    subscribe(PUB_SUB_EVENTS.cartUpdate, function () {
      setTimeout(function () {
        refreshCartMilestones(document);
      }, 50);
    });
  }

  // Optional: also listen for any custom DOM events your theme may fire
  [
    'cart:drawer:open',
    'cart:updated',
    'cart:item:added',
    'cart:item:removed',
    'ajaxCart:updated',
    'theme:cart:change',
  ].forEach(function (ev) {
    document.addEventListener(ev, function () {
      setTimeout(function () {
        refreshCartMilestones(document);
      }, 50);
    });
  });
})();


/* ============================================================
   Cart Milestones: bar + circles + modal
   ============================================================ */
(function () {
  // --- Simple /cart.js fetch helper ---
  function fetchCart(callback) {
    fetch('/cart.js', { credentials: 'same-origin' })
      .then(function (res) { return res.json(); })
      .then(function (cart) { callback(cart); })
      .catch(function (err) { console.warn('Milestones cart fetch failed', err); });
  }

  // Modal opener (uses selected reward image as main hero image)
  function openMilestoneModal(root, milestone) {
    var modal          = root.querySelector('[data-role="modal"]');
    if (!modal) return;

    var titleEl        = modal.querySelector('[data-role="modal-title"]');
    var subtitleEl     = modal.querySelector('[data-role="modal-subtitle"]');
    var bodyEl         = modal.querySelector('[data-role="modal-body"]');
    var imgEl          = modal.querySelector('[data-role="modal-image"]');
    var productTitleEl = modal.querySelector('[data-role="modal-product-title"]');

    if (titleEl) {
      titleEl.textContent = milestone.title || '';
    }

    if (subtitleEl) {
      if (milestone.unlocked) {
        subtitleEl.textContent = 'Reward unlocked!';
      } else if (milestone.money) {
        subtitleEl.textContent = 'Spend ' + milestone.money + ' to unlock.';
      } else {
        subtitleEl.textContent = '';
      }
    }

    if (bodyEl) {
      bodyEl.textContent = milestone.description || '';
    }
    if (productTitleEl) {
      productTitleEl.textContent = '';
    }

    // Remove any previous reward UI
    var oldReward = modal.querySelector('[data-role="reward-root"]');
    if (oldReward && oldReward.parentNode) {
      oldReward.parentNode.removeChild(oldReward);
    }

    // Helper: fallback to milestone image if no product image
    function setFallbackImage() {
      if (!imgEl) return;
      if (milestone.image) {
        imgEl.src = milestone.image;
      } else if (milestone.circle_icon) {
        imgEl.src = milestone.circle_icon;
      } else {
        imgEl.removeAttribute('src');
      }
    }

    var hasRewardSelector = false;

    // If unlocked and we have a reward template, inject it
    if (milestone.unlocked && milestone.templateId) {
      var tpl = root.querySelector('#' + milestone.templateId);
      if (tpl && 'content' in tpl) {
        var rewardRoot = document.createElement('div');
        rewardRoot.setAttribute('data-role', 'reward-root');
        rewardRoot.appendChild(tpl.content.cloneNode(true));

        if (bodyEl && bodyEl.parentNode) {
          bodyEl.parentNode.insertBefore(rewardRoot, bodyEl.nextSibling);
        } else {
          modal.appendChild(rewardRoot);
        }

        var selectEl = rewardRoot.querySelector('[data-role="reward-select"]');
        var addBtn   = rewardRoot.querySelector('[data-role="reward-add"]');
        hasRewardSelector = !!selectEl;

        function updatePreview() {
          if (!selectEl) return;
          var opt = selectEl.options[selectEl.selectedIndex];
          if (!opt) return;

          var src   = opt.getAttribute('data-image');
          var label = (opt.textContent || '').trim();

          // 👉 main hero image in the modal
          if (src && imgEl) {
            imgEl.src = src;
          } else {
            setFallbackImage();
          }

          if (productTitleEl) {
            productTitleEl.textContent = label || '';
          }
        }

        if (selectEl) {
          selectEl.addEventListener('change', updatePreview);
          updatePreview(); // initial
        }

        if (selectEl && addBtn) {
          addBtn.addEventListener('click', function () {
            var variantId = selectEl.value;
            if (!variantId) return;
            if (window.cmAddRewardToCart) {
              window.cmAddRewardToCart(variantId, milestone.title);
            }
          });
        }
      }
    }

    // If there was no reward selector, use the standard milestone image
    if (!hasRewardSelector) {
      setFallbackImage();
    }

    modal.classList.add('is-open');
  }

  // Add reward, then hard-refresh so cart + drawer + milestones are in sync
  window.cmAddRewardToCart = function (variantId, label) {
    if (!variantId) return;

    var payload = {
      items: [
        {
          id: Number(variantId),
          quantity: 1,
          properties: {
            _free_gift: label || 'Milestone reward'
          }
        }
      ]
    };

    fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      credentials: 'same-origin',
      body: JSON.stringify(payload)
    })
      .then(function (res) {
        if (!res.ok) throw new Error('Add to cart failed');
        return res.json();
      })
      .then(function () {
        window.location.reload();
      })
      .catch(function (err) {
        console.warn('Failed to add reward item', err);
        window.location.reload();
      });
  };

  function attachModalClose(root) {
    if (root.dataset.cmModalBound === '1') return;
    root.dataset.cmModalBound = '1';

    var modal = root.querySelector('[data-role="modal"]');
    if (!modal) return;

    modal.querySelectorAll('[data-role="modal-close"]').forEach(function (el) {
      el.addEventListener('click', function () {
        modal.classList.remove('is-open');
      });
    });

    document.addEventListener('keydown', function (evt) {
      if (evt.key === 'Escape') modal.classList.remove('is-open');
    });
  }

  function recalcRoot(root, cart) {
    if (!root) return;

    var headlineEl = root.querySelector('[data-role="headline"]');
    var fillEl     = root.querySelector('[data-role="fill"]');
    var markersEl  = root.querySelector('[data-role="markers"]');
    var dataEls    = Array.prototype.slice.call(
      root.querySelectorAll('.cm-milestone-data')
    );

    if (!dataEls.length || !fillEl || !markersEl) return;

    var subtotalCents = cart ? (cart.items_subtotal_price || cart.total_price || 0) : 0;
    var subtotalMajor = subtotalCents / 100;

    var milestones = dataEls.map(function (el) {
      var spend = parseFloat(el.getAttribute('data-m-spend')) || 0;
      return {
        spend: spend,
        title:        el.getAttribute('data-m-title')        || '',
        description:  el.getAttribute('data-m-description')  || '',
        money:        el.getAttribute('data-m-money')        || '',
        circle_icon:  el.getAttribute('data-m-circle')       || '',
        image:        el.getAttribute('data-m-image')        || '',
        templateId:   el.getAttribute('data-m-template-id')  || '',
        unlocked: subtotalMajor >= spend
      };
    }).sort(function (a, b) { return a.spend - b.spend; });

    var largest    = 0;
    var hasNext    = false;
    var nextTitle  = '';
    var nextTarget = 0;

    milestones.forEach(function (m) {
      if (m.spend > largest) largest = m.spend;
      if (!hasNext && m.spend > subtotalMajor) {
        hasNext    = true;
        nextTitle  = m.title || 'your next reward';
        nextTarget = m.spend;
      }
    });

    if (!hasNext && largest > 0) {
      nextTarget = largest;
    }

    // ----- dots -----
    markersEl.innerHTML = '';
    if (largest > 0) {
      milestones.forEach(function (m) {
        var marker = document.createElement('button');
        marker.type = 'button';
        marker.className = 'cm-progress-marker';

        var pos = (m.spend / largest) * 100;
        marker.style.left = pos + '%';

        if (m.spend <= subtotalMajor) {
          marker.classList.add('is-complete');
        }
        if (hasNext && m.spend === nextTarget && subtotalMajor < largest) {
          marker.classList.add('is-next-marker');
        }

        if (m.circle_icon) {
          marker.style.backgroundImage = 'url(' + m.circle_icon + ')';
          marker.style.setProperty('--cm-marker-image', 'url(' + m.circle_icon + ')');
        }

        marker.addEventListener('click', function () {
          openMilestoneModal(root, m);
        });

        markersEl.appendChild(marker);
      });
    }

    // ----- bar fill (overall progress) -----
    var pct = 0;
    if (largest > 0) pct = (subtotalMajor / largest) * 100;
    pct = Math.max(0, Math.min(100, Math.floor(pct)));
    var visiblePct = pct > 0 && pct < 2 ? 2 : pct; // tiny bar still visible
    fillEl.style.width = visiblePct + '%';

    // ----- headline text (to next milestone) -----
    var pctToNext = 100;
    if (hasNext && nextTarget > 0) {
      pctToNext = (subtotalMajor / nextTarget) * 100;
    }
    pctToNext = Math.max(0, Math.min(100, Math.floor(pctToNext)));

    if (headlineEl) {
      if (hasNext && subtotalMajor < largest) {
        headlineEl.textContent =
          "Keep spending! You're " + pctToNext + "% of the way to " + nextTitle + "!";
      } else {
        headlineEl.textContent = "🎉 All milestones unlocked!";
      }
    }

    attachModalClose(root);
  }

  // Fetch cart once and update all milestones in the given scope
  function refreshCartMilestones(scope) {
    var context = scope || document;
    var roots = Array.prototype.slice.call(
      context.querySelectorAll('[data-cart-milestones]')
    );

    // If roots are missing, try initializing the section first (drawer injection)
    if (!roots.length && window.renderDrawerMilestones) {
      window.renderDrawerMilestones();
      return;
    }
    if (!roots.length) return;

    fetchCart(function (cart) {
      roots.forEach(function (root) {
        recalcRoot(root, cart);
      });
    });
  }

  // Expose globally so cart-drawer can call it when it injects the section
  window.initCartMilestones = refreshCartMilestones;

  // Initial run on page load (for cart / offers page etc.)
  document.addEventListener('DOMContentLoaded', function () {
    refreshCartMilestones(document);
  });

  // Hook into Dawn's cart pub/sub so it updates AFTER any cart change
  if (typeof subscribe === 'function' && window.PUB_SUB_EVENTS && PUB_SUB_EVENTS.cartUpdate) {
    subscribe(PUB_SUB_EVENTS.cartUpdate, function () {
      setTimeout(function () {
        refreshCartMilestones(document);
      }, 50);
    });
  }

  // Optional: also listen for any custom DOM events your theme may fire
  [
    'cart:drawer:open',
    'cart:updated',
    'cart:item:added',
    'cart:item:removed',
    'ajaxCart:updated',
    'theme:cart:change'
  ].forEach(function (ev) {
    document.addEventListener(ev, function () {
      setTimeout(function () {
        refreshCartMilestones(document);
      }, 50);
    });
  });
})();


